import { skipToken } from "@tanstack/react-query"
import { createFileRoute } from "@tanstack/react-router"
import { useTranslation } from "react-i18next"
import { z } from "zod"
import { ClientHeader } from "@/components/client-header"
import { ClientTabNavigation } from "@/components/client-tab-navigation"
import { StepNavigation } from "@/components/forms/step-navigation"
import { StepRenderer } from "@/components/forms/step-renderer"
import { useStepNavigation } from "@/components/forms/use-step-navigation"
import { Page } from "@/components/page"
import { Card, CardContent } from "@/components/ui/card"
import { STEPS } from "@/constants/steps"
import { trpc } from "@/lib/trpc"

type StepId = (typeof STEPS)[number]["id"]

// Define search params schema for step navigation with proper validation
// Use .optional().catch() to handle removed steps (like "goals") gracefully by defaulting to "intro"
const searchSchema = z.object({
  currentStep: z
    .enum([...STEPS.map((s) => s.id)] as [StepId, ...StepId[]])
    .optional()
    .catch("intro"),
})

export const Route = createFileRoute("/_authed/_professional/professional/guided-forms/$estateId")({
  validateSearch: (search) => searchSchema.parse(search),
  component: RouteComponent,
})

function RouteComponent() {
  const { estateId } = Route.useParams()
  const search = Route.useSearch()
  const { t } = useTranslation()

  const {
    data: guidedForms,
    isLoading: guidedFormsLoading,
    error: guidedFormsError,
  } = trpc.listGuidedForms.useQuery({
    estateId: estateId ?? skipToken,
  })

  const stepNavigation = useStepNavigation({ currentStep: search.currentStep ?? "intro" })

  if (guidedFormsLoading) {
    return (
      <Page
        headerSlot={<ClientHeader estateId={estateId} />}
        tabSlot={<ClientTabNavigation estateId={estateId} />}
      >
        <div>{t("pages.reports.estate.loading.content")}</div>
      </Page>
    )
  }

  if (guidedFormsError || !guidedForms) {
    return (
      <Page
        headerSlot={<ClientHeader estateId={estateId} />}
        tabSlot={<ClientTabNavigation estateId={estateId} />}
      >
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
          <p className="font-medium text-red-800">{t("pages.reports.estate.error.message")}</p>
          <p className="text-red-700">
            {guidedFormsError?.message || t("pages.reports.estate.error.estateNotFound")}
          </p>
        </div>
      </Page>
    )
  }
  return (
    <Page
      headerSlot={<ClientHeader estateId={estateId} />}
      tabSlot={<ClientTabNavigation estateId={estateId} />}
    >
      <div className="space-y-6">
        <Card>
          <CardContent>
            <div className="space-y-6">
              <StepNavigation {...stepNavigation} />
              <StepRenderer
                currentStep={stepNavigation.currentStep}
                estateId={estateId}
                isUserLoading={false}
                isUserError={false}
                onNext={stepNavigation.goToNextStep}
              />
            </div>
          </CardContent>
        </Card>
      </div>
    </Page>
  )
}
